/**
 * AuthenticatedApp.tsx
 *
 * Wrapper component that handles authentication.
 * Shows PIN screen if not authenticated, otherwise renders the main App.
 */

import React, { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import PinScreen from './components/PinScreen';
import App from './App';

// Inner component that uses auth context
function AuthGate() {
  const { isAuthenticated, isLoading, authEnabled } = useAuth();
  const [emcogName, setEmcogName] = useState<string>('EmCog');

  // Fetch EmCog name for PIN screen
  useEffect(() => {
    async function fetchName() {
      try {
        // Try to get the name from the API (this endpoint doesn't require auth)
        const baseUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';
        const res = await fetch(`${baseUrl}/api/names`);
        if (res.ok) {
          const data = await res.json();
          if (data.emcogName) {
            setEmcogName(data.emcogName);
          }
        }
      } catch (error) {
        // Ignore errors, use default name
        console.log('Could not fetch EmCog name, using default');
      }
    }
    fetchName();
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div className="auth-loading">
        <div className="auth-loading-spinner"></div>
      </div>
    );
  }

  // Not authenticated - show PIN screen
  if (authEnabled && !isAuthenticated) {
    return <PinScreen emcogName={emcogName} />;
  }

  // Authenticated - render main app
  return <App />;
}

// Main wrapper with AuthProvider
export default function AuthenticatedApp() {
  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  );
}
